import { departments, DepartmentData, DepartmentManager } from "@/mocks/rop";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

function WidgetCard({ icon, label, count, amount, iconBg, color }: { icon: string; label: string; count: number; amount?: number; iconBg: string; color: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#D5CA8A] flex-1 min-w-0">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <i className={`${icon} text-[18px] ${color}`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#666666] leading-tight truncate">{label}</p>
        <p className="text-[18px] font-bold text-[#2D2D2D] leading-tight mt-0.5">{count}</p>
        {amount !== undefined && (
          <p className="text-[10px] text-[#666666] leading-tight">{formatMoney(amount)}</p>
        )}
      </div>
    </div>
  );
}

export default function DepartmentTab({ departmentIndex }: { departmentIndex: number }) {
  const dept: DepartmentData = departments[departmentIndex];
  const managers: DepartmentManager[] = dept.managers;

  const totals = {
    activeDeals: managers.reduce((s, m) => s + m.activeDeals, 0),
    activeDealsAmount: managers.reduce((s, m) => s + m.activeDealsAmount, 0),
    unansweredOffers: managers.reduce((s, m) => s + m.unansweredOffers, 0),
    unansweredOffersAmount: managers.reduce((s, m) => s + m.unansweredOffersAmount, 0),
    ordersInProgress: managers.reduce((s, m) => s + m.ordersInProgress, 0),
    ordersInProgressAmount: managers.reduce((s, m) => s + m.ordersInProgressAmount, 0),
    warehouseAssembly: managers.reduce((s, m) => s + m.warehouseAssembly, 0),
    deliveriesToday: managers.reduce((s, m) => s + m.deliveriesToday, 0),
    clientsWithDebt: managers.reduce((s, m) => s + m.clientsWithDebt, 0),
    debtAmount: managers.reduce((s, m) => s + m.debtAmount, 0),
    paymentsToday: managers.reduce((s, m) => s + m.paymentsToday, 0),
    paymentsAmount: managers.reduce((s, m) => s + m.paymentsAmount, 0),
  };

  return (
    <div>
      {/* Department Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-building-2-line text-[16px] text-[#2D2D2D]"></i>
          <span className="text-[13px] font-semibold text-[#2D2D2D]">{dept.name}</span>
          <span className="text-[10px] text-[#666666] bg-[#FDF2B5] px-2 py-0.5 rounded-full">{managers.length} {managers.length === 1 ? 'менеджер' : managers.length >= 2 && managers.length <= 4 ? 'менеджера' : 'менеджеров'}</span>
        </div>
        <span className="text-[10px] text-[#666666]">Обновлено: сегодня, 09:30</span>
      </div>

      {/* Widgets Row */}
      <div className="flex flex-nowrap gap-2 mb-4 overflow-x-auto">
        <WidgetCard
          icon="ri-focus-3-line" label="Активные сделки" count={totals.activeDeals} amount={totals.activeDealsAmount}
          iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
        />
        <WidgetCard
          icon="ri-mail-unread-line" label="КП без ответа" count={totals.unansweredOffers} amount={totals.unansweredOffersAmount}
          iconBg="bg-[#FEF3C7]" color="text-[#D97706]"
        />
        <WidgetCard
          icon="ri-file-list-3-line" label="Заказы в работе" count={totals.ordersInProgress} amount={totals.ordersInProgressAmount}
          iconBg="bg-[#E8F4FD]" color="text-[#0369A1]"
        />
        <WidgetCard
          icon="ri-tools-line" label="Сборка на складе" count={totals.warehouseAssembly}
          iconBg="bg-[#FDF2B5]" color="text-[#B45309]"
        />
        <WidgetCard
          icon="ri-truck-line" label="Доставки сегодня" count={totals.deliveriesToday}
          iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
        />
        <WidgetCard
          icon="ri-money-dollar-circle-line" label="Клиенты с долгом" count={totals.clientsWithDebt} amount={totals.debtAmount}
          iconBg="bg-[#FEE2E2]" color="text-[#DC2626]"
        />
        <WidgetCard
          icon="ri-bank-card-line" label="Оплаты сегодня" count={totals.paymentsToday} amount={totals.paymentsAmount}
          iconBg="bg-[#D4EDDA]" color="text-[#2D6A4F]"
        />
      </div>

      {/* Manager Table */}
      <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
          <div className="flex items-center gap-1.5">
            <i className="ri-team-line text-[12px] text-[#2D2D2D]"></i>
            <span className="text-[11px] font-semibold text-[#2D2D2D]">Менеджеры отдела</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-[#FDF2B5]/50 border-b border-[#D5CA8A]">
                <th className="text-left px-3 py-2 text-[#666666] font-medium whitespace-nowrap">Менеджер</th>
                <th className="text-left px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Грейд</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Рейтинг</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Активные сделки</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">КП без ответа</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Заказы в работе</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Сборка на складе</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Доставки сегодня</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Клиенты с долгом</th>
                <th className="text-center px-2 py-2 text-[#666666] font-medium whitespace-nowrap">Оплаты сегодня</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((m) => (
                <tr key={m.name} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors">
                  <td className="px-3 py-2.5 text-[#2D2D2D] font-medium whitespace-nowrap">{m.name}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                      m.grade === "Ведущий менеджер"
                        ? "bg-[#FEF3C7] text-[#D97706]"
                        : "bg-[#E8F4FD] text-[#0369A1]"
                    }`}>
                      {m.grade}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className={`text-[11px] font-bold ${
                      m.rating >= 11 ? "text-[#2D6A4F]" :
                      m.rating >= 8 ? "text-[#D97706]" :
                      "text-[#DC2626]"
                    }`}>{m.rating}/{m.maxRating}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#2D2D2D] font-medium">{m.activeDeals}</span>
                    <span className="text-[#666666] ml-1">{formatMoney(m.activeDealsAmount)}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#D97706] font-medium">{m.unansweredOffers}</span>
                    <span className="text-[#666666] ml-1">{formatMoney(m.unansweredOffersAmount)}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#0369A1] font-medium">{m.ordersInProgress}</span>
                    <span className="text-[#666666] ml-1">{formatMoney(m.ordersInProgressAmount)}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#B45309] font-medium">{m.warehouseAssembly}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#2D6A4F] font-medium">{m.deliveriesToday}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#DC2626] font-medium">{m.clientsWithDebt}</span>
                    <span className="text-[#666666] ml-1">{formatMoney(m.debtAmount)}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <span className="text-[#2D6A4F] font-medium">{m.paymentsToday}</span>
                    <span className="text-[#666666] ml-1">{formatMoney(m.paymentsAmount)}</span>
                  </td>
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-[#FEF3C7] font-semibold">
                <td className="px-3 py-2.5 text-[#2D2D2D] whitespace-nowrap">ИТОГО</td>
                <td className="px-2 py-2.5 whitespace-nowrap"></td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap">
                  <span className="text-[11px] font-bold text-[#666666]">
                    {(managers.reduce((s, m) => s + m.rating, 0) / managers.length).toFixed(1)}/{managers[0]?.maxRating || 15}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#2D2D2D]">
                  {totals.activeDeals} <span className="text-[#666666] font-normal">{formatMoney(totals.activeDealsAmount)}</span>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#D97706]">
                  {totals.unansweredOffers} <span className="text-[#666666] font-normal">{formatMoney(totals.unansweredOffersAmount)}</span>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#0369A1]">
                  {totals.ordersInProgress} <span className="text-[#666666] font-normal">{formatMoney(totals.ordersInProgressAmount)}</span>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#B45309]">{totals.warehouseAssembly}</td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#2D6A4F]">{totals.deliveriesToday}</td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#DC2626]">
                  {totals.clientsWithDebt} <span className="text-[#666666] font-normal">{formatMoney(totals.debtAmount)}</span>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap text-[#2D6A4F]">
                  {totals.paymentsToday} <span className="text-[#666666] font-normal">{formatMoney(totals.paymentsAmount)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}